import React, { Component } from 'react';
import PropTypes from 'prop-types';

import SlateDialog from 'components/Shared/SlateDialog/SlateDialog';
import SlateTextInput from 'components/Shared/SlateTextInput';
import { validateContact } from 'store/actions/inspectLanding';

// Confirm dialog for Request Access. Each send captures the account uuid and a
// send generation; a send whose account or generation is no longer current
// (dialog closed, reset, reopened, stack switched, unmounted) changes nothing.
export default class RequestAccessDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      error: null,
      email: props.contactEmail || '',
      phone: props.contactPhone || '',
      fieldErrors: {},
    };
    this._isMounted = false;
    this._sendGeneration = 0;
    this._sending = false;
    this.handleSend = this.handleSend.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handlePhoneChange = this.handlePhoneChange.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) {
      this._sendGeneration += 1;
      this._sending = false;
      this.setState({ submitting: false, error: null, fieldErrors: {} });
    }
    if (!prevProps.open && this.props.open) {
      this.setState({
        email: this.props.contactEmail || '',
        phone: this.props.contactPhone || '',
        fieldErrors: {},
        error: null,
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._sendGeneration += 1;
  }

  handleClose() {
    if (this.state.submitting) return;
    this.props.onClose();
  }

  clearFieldError(name) {
    this.setState(function (prev) {
      var next = Object.assign({}, prev.fieldErrors);
      delete next[name];
      return { fieldErrors: next };
    });
  }

  handleEmailChange(e) {
    this.setState({ email: e.target.value });
    this.clearFieldError('contactEmail');
  }

  handlePhoneChange(e) {
    this.setState({ phone: e.target.value });
    this.clearFieldError('contactPhone');
  }

  handleSend() {
    if (this._sending) return;
    var self = this;
    var contact = { contactEmail: this.state.email, contactPhone: this.state.phone };
    var invalid = validateContact(contact);
    var invalidNames = Object.keys(invalid);
    if (invalidNames.length > 0) {
      var translated = {};
      invalidNames.forEach(function (name) {
        translated[name] = self.props.t(invalid[name]);
      });
      this.setState({ fieldErrors: translated });
      return;
    }
    var sentFor = this.props.accountUUID;
    this._sendGeneration += 1;
    var generation = this._sendGeneration;
    this._sending = true;
    this.setState({ submitting: true, error: null, fieldErrors: {} });

    function isStale() {
      return (
        !self._isMounted ||
        generation !== self._sendGeneration ||
        self.props.accountUUID !== sentFor
      );
    }

    this.props.onSend(contact).then(
      function (result) {
        if (isStale()) return;
        self._sending = false;
        self.setState({ submitting: false });
        self.props.onSent(result && result.status, sentFor);
        self.props.onClose();
      },
      function (err) {
        if (isStale()) return;
        self._sending = false;
        var apiErrors = err && err.dataErrors;
        if (apiErrors && (apiErrors.contactEmail || apiErrors.contactPhone)) {
          self.setState({
            submitting: false,
            fieldErrors: {
              contactEmail: apiErrors.contactEmail,
              contactPhone: apiErrors.contactPhone,
            },
          });
          return;
        }
        self.setState({
          submitting: false,
          error: (err && err.message) || self.props.t('messages:genericError'),
        });
      },
    );
  }

  render() {
    var props = this.props;
    var t = props.t;
    var submitting = this.state.submitting;
    var fieldErrors = this.state.fieldErrors;
    return (
      <SlateDialog
        open={props.open}
        onClose={this.handleClose}
        title={t('inspectLanding:dialogTitle')}
        maxWidth="xs"
        closable={!submitting}
        actions={[
          {
            key: 'cancel',
            label: t('inspectLanding:cancel'),
            variant: 'secondary',
            onClick: this.handleClose,
            disabled: submitting,
          },
          {
            key: 'send',
            label: t('inspectLanding:sendRequest'),
            variant: 'primary',
            onClick: this.handleSend,
            loading: submitting,
          },
        ]}
      >
        <p className="inspect-landing-dialog-body">
          {t('inspectLanding:dialogBody', { stack: props.stackName })}
        </p>
        <div className="inspect-landing-request-fields">
          <SlateTextInput
            name="contactEmail"
            type="email"
            autoComplete="email"
            label={t('inspectLanding:contactEmail')}
            fullWidth
            value={this.state.email}
            onChange={this.handleEmailChange}
            error={fieldErrors.contactEmail || ''}
            disabled={submitting}
          />
          <SlateTextInput
            name="contactPhone"
            type="tel"
            autoComplete="tel"
            label={t('inspectLanding:contactPhone')}
            fullWidth
            value={this.state.phone}
            onChange={this.handlePhoneChange}
            error={fieldErrors.contactPhone || ''}
            disabled={submitting}
          />
        </div>
        {this.state.error && (
          <div className="auth-form-error">{this.state.error}</div>
        )}
      </SlateDialog>
    );
  }
}

RequestAccessDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onSent: PropTypes.func.isRequired,
  accountUUID: PropTypes.string,
  contactEmail: PropTypes.string,
  contactPhone: PropTypes.string,
  stackName: PropTypes.string,
  t: PropTypes.func.isRequired,
};
